import React, { useCallback, useMemo, useRef } from "react";

import { useTranslation } from "utils";

import { Flex, Icon, Typography } from "components";

import { ResilienceScoreBreakdownCard } from "pages/app/resilience-score/components/resilience-score-context-drawer/ResilienceScoreBreakdownCard";
import { type BreakdownCardProps, avg } from "pages/app/resilience-score/utils/ResilienceScoreContextDrawerUtils";
import {
  FarmResilienceApiResponse,
  FarmResilienceFieldEntry,
  FarmResilienceFieldYear,
  RESILIENCE_SCORE_LEVEL_2,
  RESILIENCE_SCORE_LEVEL_3,
  calcPct,
  deriveRiskLevel,
  mapTrendDirectionFromDelta,
} from "pages/app/resilience-score/utils/ResilienceScoreUtils";

type ResilienceScoreBreakdownSectionProps = {
  selectedYear: number;
  farmResilienceScore: FarmResilienceApiResponse | null | undefined;
  years: number[];
  maxScore?: number;
};

const clampScore = (value: number | null | undefined, maxScore: number) => {
  if (value == null || Number.isNaN(value)) return null;
  return Math.max(0, Math.min(maxScore, value));
};

const getYearDataFromField = (field: FarmResilienceFieldEntry, year: number): FarmResilienceFieldYear | null => {
  const raw = field[year];
  if (!raw || typeof raw !== "object") return null;
  return raw as FarmResilienceFieldYear;
};

type MetricKey = RESILIENCE_SCORE_LEVEL_2;
type L3Key = RESILIENCE_SCORE_LEVEL_3;

function getMetricScore(entry: FarmResilienceFieldYear, metric: MetricKey): number | null {
  const section = entry[metric];
  return section?.score ?? null;
}

function getL3Score(entry: FarmResilienceFieldYear, metric: MetricKey, key: L3Key): number | null {
  const section = entry[metric];
  const value = section?.[key];
  if (typeof value !== "number") return null;
  return value;
}

function buildCategoryCard(
  title: string,
  metric: MetricKey,
  l3: Array<{ key: L3Key; label: string }>,
  selectedYear: number,
  years: number[],
  response: FarmResilienceApiResponse | null | undefined,
  maxScore: number
): BreakdownCardProps {
  const fields = (response ?? []) as FarmResilienceFieldEntry[];

  const currentScores = fields.map((f) =>
    clampScore(getMetricScore(getYearDataFromField(f, selectedYear) ?? ({} as FarmResilienceFieldYear), metric), maxScore)
  );
  const current = avg(currentScores);

  const prevScores = fields.map((f) =>
    clampScore(getMetricScore(getYearDataFromField(f, selectedYear - 1) ?? ({} as FarmResilienceFieldYear), metric), maxScore)
  );
  const previous = avg(prevScores);
  const delta = current != null && previous != null ? current - previous : null;
  const changePct = calcPct(delta, previous);
  const riskLevel = current != null ? deriveRiskLevel(current) : null;

  const rows = l3.map(({ key, label }) => {
    const thisYear = avg(
      fields.map((f) => {
        const yd = getYearDataFromField(f, selectedYear);
        if (!yd) return null;
        return clampScore(getL3Score(yd, metric, key), maxScore);
      })
    );
    const lastYear = avg(
      fields.map((f) => {
        const yd = getYearDataFromField(f, selectedYear - 1);
        if (!yd) return null;
        return clampScore(getL3Score(yd, metric, key), maxScore);
      })
    );
    const delta = thisYear != null && lastYear != null ? thisYear - lastYear : null;
    const pct = calcPct(delta, lastYear);
    return {
      label,
      value: thisYear,
      trend: mapTrendDirectionFromDelta(delta),
      changePct: pct,
    };
  });

  const miniTimeline = years.map((year) => {
    const yearValues = fields.map((f) => {
      const yd = getYearDataFromField(f, year);
      if (!yd) return null;
      return clampScore(getMetricScore(yd, metric), maxScore);
    });
    const yearAvg = avg(yearValues);
    const hasAnyData = yearValues.some((value) => value != null);
    return { year, value: hasAnyData ? yearAvg : null, unknown: !hasAnyData };
  });

  return {
    title,
    score: current,
    maxScore,
    riskLevel,
    changePct,
    metricRows: rows,
    timeline: miniTimeline,
    selectedYear,
  };
}

export const ResilienceScoreBreakdownSection = ({ selectedYear, farmResilienceScore, years, maxScore = 10 }: ResilienceScoreBreakdownSectionProps) => {
  const { t } = useTranslation(["resilienceScore"]);
  const cards = useMemo(
    () => [
      buildCategoryCard(
        t("resilienceScoreContextDrawer.breakdown.categories.soilHealthScore", "Soil Health Score"),
        RESILIENCE_SCORE_LEVEL_2.SOIL_HEALTH,
        [
          { key: RESILIENCE_SCORE_LEVEL_3.TILLAGE_SCORE, label: t("resilienceScoreContextDrawer.breakdown.metrics.tillageScore", "Tillage Score") },
          {
            key: RESILIENCE_SCORE_LEVEL_3.CROP_ROTATION_SCORE,
            label: t("resilienceScoreContextDrawer.breakdown.metrics.cropRotationScore", "Crop Rotation Score"),
          },
          { key: RESILIENCE_SCORE_LEVEL_3.SOIL_COVER_SCORE, label: t("resilienceScoreContextDrawer.breakdown.metrics.soilCoverScore", "Soil Cover Score") },
        ],
        selectedYear,
        years,
        farmResilienceScore,
        maxScore
      ),
      buildCategoryCard(
        t("resilienceScoreContextDrawer.breakdown.categories.waterManagementScore", "Water Management Score"),
        RESILIENCE_SCORE_LEVEL_2.WATER_CAPACITY,
        [
          { key: RESILIENCE_SCORE_LEVEL_3.TILLAGE_SCORE, label: t("resilienceScoreContextDrawer.breakdown.metrics.tillageScore", "Tillage Score") },
          { key: RESILIENCE_SCORE_LEVEL_3.SOIL_COVER_SCORE, label: t("resilienceScoreContextDrawer.breakdown.metrics.soilCoverScore", "Soil Cover Score") },
        ],
        selectedYear,
        years,
        farmResilienceScore,
        maxScore
      ),
      buildCategoryCard(
        t("resilienceScoreContextDrawer.breakdown.categories.biodiversityScore", "Biodiversity Score"),
        RESILIENCE_SCORE_LEVEL_2.BIODIVERSITY,
        [
          {
            key: RESILIENCE_SCORE_LEVEL_3.CROP_ROTATION_SCORE,
            label: t("resilienceScoreContextDrawer.breakdown.metrics.cropRotationScore", "Crop Rotation Score"),
          },
        ],
        selectedYear,
        years,
        farmResilienceScore,
        maxScore
      ),
    ],
    [farmResilienceScore, maxScore, selectedYear, t, years]
  );

  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const scrollByCards = useCallback((dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>(".rsd-breakdown-card");
    const dx = (card?.offsetWidth ?? 380) + 16;
    el.scrollBy({ left: dir * dx, behavior: "smooth" });
  }, []);

  return (
    <Flex vertical className="rsd-breakdown" gap="0.75rem">
      <Flex justify="space-between" align="center" className="rsd-breakdown-header">
        <Typography variant="paragraph-l" className="rsd-breakdown-title">
          {t("resilienceScoreContextDrawer.breakdown.title", "Score Breakdown")}
        </Typography>
        <Flex align="center" gap="0.75rem" className="rsd-breakdown-controls">
          <Flex align="center" gap="0.25rem" className="rsd-breakdown-arrows">
            <button type="button" className="rsd-breakdown-arrow" onClick={() => scrollByCards(-1)} aria-label={"Previous"}>
              <Icon iconName="tabler/chevron-left" size="s" />
            </button>
            <button type="button" className="rsd-breakdown-arrow" onClick={() => scrollByCards(1)} aria-label={"Next"}>
              <Icon iconName="tabler/chevron-right" size="s" />
            </button>
          </Flex>
        </Flex>
      </Flex>

      <div className="rsd-breakdown-scroller" ref={scrollerRef}>
        {cards.map((card) => (
          <ResilienceScoreBreakdownCard key={card.title} {...card} />
        ))}
      </div>
    </Flex>
  );
};
